import React, { useEffect, useRef, useState } from "react";
import BreadcrumbOrder from "../Components/Layout/BreadcrumbOrder";
import { useNavigate, useParams } from "react-router-dom";
import Cart from "../Components/Layout/Cart/Cart";
import { ORDER_TYPE_ICONS, ORDER_TYPE_LABELS } from "../Utils/Constants/Service";
import { TbBuildingSkyscraper, TbClock, TbHash, TbMessage2Question } from "react-icons/tb";
import { formatAddressStore, vibrate } from "../Utils/Helpers";
import { useStoreContext } from "../Contexts/StoreContext";
import Badge from "../Components/Layout/Badge";
import DefaultLayout from "../Layouts/DefaultLayout";
import { useOrderTypeContext } from "../Contexts/OrderTypeContext";
import OrderService from "../Services/OrderService";
import { useLoginContext } from "../Contexts/LoginContext";
import { METHOD_PAYMENT_TYPE_PIX, ORDER_STATUS_AWAITING_PAYMENT, ORDER_STATUS_CANCELED, ORDER_STATUS_DONE, ORDER_STATUS_EXPEDITED, ORDER_STATUS_EXPIRED, ORDER_STATUS_MOUNTING, ORDER_STATUS_OPEN, ORDER_STATUS_PENDING, ORDER_STATUS_RELEASED_SHIPMENT, ORDER_STATUS_RELEASED_TAKEAWAY } from "../Utils/Constants/Checkout";
import { useCheckoutContext } from "../Contexts/CheckoutContext";
import { useLayoutContext } from "../Contexts/LayoutContext";
import { useCartHook } from "../Hooks/CartHook";
import { useCartContext } from "../Contexts/CartContext";
import { ImSpinner8 } from "react-icons/im";
import { getOrderColorByStatusIdToPlaced, ORDER_CART_NOT_FOUND } from "../Utils/Constants/Order";
import { useStoreHook } from "../Hooks/StoreHook";
import QRCodePix from "../Components/Layout/Checkout/QRCodePix";
import StampStatus from "../Components/Layout/StampStatus";
import { formatCartId } from "../Utils/Formatters";

const OrderPlaced = () => {

    const { order_id } = useParams();
    const { currentStore } = useStoreContext();
    const { orderType } = useOrderTypeContext();
    const { token, guestToken } = useLoginContext();
    const { order, setOrder, showPixCode, setShowPixCode } = useCheckoutContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingIconSuccess, setMainLoadingTitle, setMainLoadingCloseButton, setMainLoadingMessage, setMainLoadingLabelCloseButton } = useLayoutContext();
    const { cartFinished, cancelPayment } = useCartHook();
    const { createGuestCart, getLinkToHome } = useStoreHook();
    const { cartDisplay } = useCartContext();

    const navigate = useNavigate();
    const { isStoreCounter, isDirectSale } = useOrderTypeContext();

    const [getOrder, setGetOrder] = useState<any | null>();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [localCart, setLocalCart] = useState<any | null>(order?.cart);
    const [loadingGetOrder, setLoadingGetOrder] = useState<boolean>(true);
    const [showStatusName, setShowStatusName] = useState<boolean>(false);
    const [showBreadcrumbOrder, setShowBreadcrumbOrder] = useState<boolean>(false);
    const [showStampStatus, setShowStampStatus] = useState<boolean>(false);
    const [readNotifyReleased, setReadNotifyReleased] = useState<boolean>(false);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (!order_id || !orderType || !currentStore) {
            navigate(getLinkToHome());
            return;
        }

        document.title = `Pedido ${formatCartId(Number(order_id))}`;

        const checkStatusToChangeStep = (status: number) => {
            switch (status) {
                case ORDER_STATUS_AWAITING_PAYMENT:
                    setCurrentStep(0);
                    break;
                case ORDER_STATUS_PENDING:
                    setCurrentStep(0);
                    break;
                case ORDER_STATUS_MOUNTING:
                    setCurrentStep(0);
                    break;
                case ORDER_STATUS_RELEASED_TAKEAWAY:
                    setCurrentStep(1);
                    break;
                case ORDER_STATUS_DONE:
                    setCurrentStep(2);
                    break;
                default:
                    break;
            }
        }

        let intervalToCheckOrderStatus: NodeJS.Timeout | null = null;

        const getDetail = async () => {
            const payload = { order_id: Number(order_id) };
            try {
                const orderService = new OrderService(orderType, token, guestToken);
                const details = await orderService.detail(payload);
                setLoadingGetOrder(false);

                if (details) {
                    if (isFirstRender.current) {
                        isFirstRender.current = false;
                    }
                    setLocalCart(details.cart);
                    setShowStatusName([ORDER_STATUS_AWAITING_PAYMENT, ORDER_STATUS_DONE, ORDER_STATUS_EXPIRED, ORDER_STATUS_CANCELED, ORDER_STATUS_RELEASED_TAKEAWAY].includes(details.status));
                    setShowBreadcrumbOrder([ORDER_STATUS_MOUNTING, ORDER_STATUS_RELEASED_TAKEAWAY, ORDER_STATUS_EXPEDITED, ORDER_STATUS_OPEN, ORDER_STATUS_RELEASED_SHIPMENT].includes(details.status));
                    checkStatusToChangeStep(details.status);
                    setShowStampStatus([ORDER_STATUS_CANCELED, ORDER_STATUS_EXPIRED].includes(details.status));
                    setGetOrder(details);
                    setOrder(details);

                    const includeIntervalToCheckOrder = ![ORDER_STATUS_DONE, ORDER_STATUS_EXPIRED, ORDER_STATUS_CANCELED].includes(details.status);
                    const isAnOrderCompletionStatus = [ORDER_STATUS_DONE, ORDER_STATUS_EXPIRED, ORDER_STATUS_CANCELED].includes(details.status);

                    if (includeIntervalToCheckOrder) {
                        if (!intervalToCheckOrderStatus) {
                            intervalToCheckOrderStatus = setInterval(() => {
                                getDetail();
                            }, 10000);
                        }
                    } else {
                        if (intervalToCheckOrderStatus) {
                            clearInterval(intervalToCheckOrderStatus);
                            intervalToCheckOrderStatus = null;
                        }
                        if (isAnOrderCompletionStatus) {
                            if (isStoreCounter) {
                                if (details.status === ORDER_STATUS_DONE) {
                                    cartFinished({ id: cartDisplay.id, order: details });
                                    navigate(getLinkToHome());
                                }
                                //! O QUE FAZER QUANDO EXPIRAR OU CANCELAR NO AUTO?
                            }
                            setOrder(null);
                        }
                    }

                    if (isFirstRender.current && order.status !== details.status && details.status !== ORDER_STATUS_AWAITING_PAYMENT) {
                        createGuestCart();
                    }

                    if (details.status === ORDER_STATUS_EXPIRED) { //! VERIFICAR
                        navigate(`${getLinkToHome()}/checkout`);
                    }

                    setShowPixCode(details.payment_method.type === METHOD_PAYMENT_TYPE_PIX && details.status === ORDER_STATUS_AWAITING_PAYMENT);
                }
            } catch (error: any) {
                setLoadingGetOrder(false);
                if (error.response?.data?.error === ORDER_CART_NOT_FOUND) {
                    if (intervalToCheckOrderStatus) {
                        clearInterval(intervalToCheckOrderStatus);
                    }
                    setMainLoading(true);
                    setMainLoadingIcon(false);
                    setMainLoadingIconSuccess(false);
                    setMainLoadingIconError(true);
                    setMainLoadingTitle('Pedido não encontrado');
                    setMainLoadingMessage(`Não localizamos o pedido <strong>${formatCartId(Number(order_id))}</strong>. Verifique e tente novamente.`);
                    setMainLoadingCloseButton(true);
                    setMainLoadingLabelCloseButton('Ok, entendi')
                    navigate(getLinkToHome());
                }
            }
        }

        getDetail();

        // Função de limpeza
        return () => {
            if (intervalToCheckOrderStatus) {
                clearInterval(intervalToCheckOrderStatus);
            }
        };


    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStoreCounter, isDirectSale]);

    useEffect(() => {
        if (getOrder && !readNotifyReleased && getOrder.status === ORDER_STATUS_RELEASED_TAKEAWAY) {
            isReleased();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getOrder])


    const isReleased = () => {
        vibrate(1500);
        setTimeout(() => {
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconSuccess(true);
            setMainLoadingTitle(`
                <div class='grid'>
                    <span>Temos uma ótima notícia!</span>
                    <span class='text-xl font-extrabold'>Seu pedido está liberado</span>
                    <span class='text-3xl font-black leading-none'>${formatCartId(Number(order_id))}</span></div>
                </span>
            `);
            setMainLoadingMessage('');
            setMainLoadingLabelCloseButton('Beleza! Vou buscar...')
            setMainLoadingCloseButton(true);
            setReadNotifyReleased(true);
        }, 1000);
    }


    const handleExpirePix = () => {
        if (isStoreCounter) {
            cancelPayment(cartDisplay?.id, token)
                .then((response: any) => {
                    if (response.success) {
                        setMainLoading(true);
                        setMainLoadingIcon(false);
                        setMainLoadingIconError(true);
                        setMainLoadingTitle('Seu código expirou.');
                        setMainLoadingMessage('Será necessário gerar novamente');
                        setMainLoadingCloseButton(true);
                        navigate(`${getLinkToHome()}/checkout`);
                    }
            });
        }
    }

    if (!order_id || !orderType || !currentStore) return null;

    return (
        <DefaultLayout>

            <StampStatus status_name={getOrder?.status_name} show={showStampStatus} />

            <section className="flex gap-6 flex-col items-center justify-between w-full h-full">
                {loadingGetOrder
                    ?
                        <div className="flex items-center justify-center gap-1 h-full text-sm text-gray-500">
                            <ImSpinner8 className="animate-spin" />
                            <span>Carregando informações do pedido</span>
                        </div>
                    :   getOrder &&
                            <>
                                {showBreadcrumbOrder &&
                                    <section className="flex w-full items-center justify-center">
                                        <BreadcrumbOrder disabled={
                                            getOrder && (getOrder.status === ORDER_STATUS_EXPIRED || getOrder.status === ORDER_STATUS_CANCELED)
                                        } currentStep={currentStep} />
                                    </section>
                                }

                                <section className='flex flex-col gap-6 h-full w-full mt-2'>
                                    <h2 className="text-2xl md:pb-2 font-bold text-gray-700 flex items-center justify-center gap-x-1.5 leading-none flex-wrap">
                                        {isStoreCounter ? 'Comanda' : 'Pedido'}
                                        <Badge type={getOrder ? getOrderColorByStatusIdToPlaced(getOrder.status) : undefined} className="text-xl"><TbHash />{ getOrder && formatCartId(Number(isStoreCounter ? getOrder.cart.id : getOrder.id)) }</Badge>
                                        {showStatusName && <span className="inline"> { getOrder.status_name.toLowerCase() }</span>}
                                    </h2>

                                    <section className="flex flex-col md:flex-row gap-4 justify-between w-full h-full transition-all ease-in-out">

                                        {/* QRCODE */}
                                        { showPixCode && <div className="flex justify-center items-center w-full h-full rounded-lg md:shadow-lg md:shadow-gray-100 md:ring-1 ring-gray-100"><QRCodePix order={getOrder} onClose={() => setShowPixCode(false)} onExpire={() => handleExpirePix} /></div>}


                                        <section className={`flex ${showPixCode ? 'flex-col' : 'flex-col md:flex-row'} gap-4 w-full h-full`}>

                                            {/* ORDER INFO */}
                                            {!isStoreCounter &&
                                                <section className="flex flex-col gap-4 w-full p-4 rounded-lg shadow-lg shadow-gray-100 ring-1 ring-gray-100">

                                                    <div className="w-full">
                                                        <h4 className="text-sm flex items-center justify-between gap-2">
                                                            <span>Forma de compra</span>
                                                        </h4>
                                                        <div className='flex items-center gap-1 text-lg font-bold text-gray-700'>
                                                            <span className="text-2xl">{ORDER_TYPE_ICONS[orderType as keyof typeof ORDER_TYPE_ICONS]}</span>
                                                            <span>{ORDER_TYPE_LABELS[orderType as keyof typeof ORDER_TYPE_LABELS]}</span>
                                                        </div>
                                                    </div>

                                                    <hr className="border-dashed border-gray-300" />

                                                    {getOrder &&
                                                        <div className="flex items-center gap-2 w-full text-sm">
                                                            <span className="self-start"><TbClock size={20} /></span>
                                                            <div className="flex flex-col leading-none">
                                                                <h4 className="leading-none font-semibold">Pedido realizado em</h4>
                                                                <p className="leading-none text-gray-500 mt-1">{ getOrder.created_at_formatted }</p>
                                                            </div>
                                                        </div>
                                                    }

                                                    {(getOrder && getOrder.observation) &&
                                                        <div className="flex items-center gap-2 w-full text-sm">
                                                            <span className="self-start"><TbMessage2Question size={20} /></span>
                                                            <div className="flex flex-col leading-none">
                                                                <h4 className="leading-none font-semibold">Observação</h4>
                                                                <p className="leading-none text-gray-500 mt-1">{ getOrder.observation }</p>
                                                            </div>
                                                        </div>
                                                    }

                                                    <div className="flex items-center gap-2 w-full text-sm">
                                                        <span className="self-start"><TbBuildingSkyscraper size={20} /></span>
                                                        <div className="flex flex-col leading-none">
                                                            <h4 className="leading-none font-semibold">Restaurante</h4>
                                                            <p className="leading-none text-gray-500 mt-1">{ formatAddressStore(currentStore) }</p>
                                                        </div>
                                                    </div>

                                                </section>
                                            }

                                            {/* CART */}
                                            <section className="w-full h-full py-4 bg-gray-50 rounded-lg ring-1 ring-gray-100">
                                                {localCart && <Cart getCart={localCart} onCloseCart={() => {}} onlyInfo />}
                                            </section>

                                        </section>

                                    </section>
                                </section>
                            </>
                }
            </section>
        </DefaultLayout>
    );
};

export default OrderPlaced;